import {
  updateLearnerNotesApi,
  useLearningProgressLearnerDetail,
} from "@/api/services/main/learning-progress";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { IconSave } from "@/assets/icons";
import { LearnerInfoReadOnlySection } from "@/features/learning-progress/components/learner-edit/LearnerInfoReadOnlySection";
import { EncouragementMessageEditSection } from "@/features/learning-progress/components/learner-edit/EncouragementMessageEditSection";
import { OrganizationReportEditSection } from "@/features/learning-progress/components/learner-edit/OrganizationReportEditSection";
import { SupportOrgMemoEditSection } from "@/features/learning-progress/components/learner-edit/SupportOrgMemoEditSection";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useLearnerNotesPermission } from "@/features/learning-progress/hooks/useLearnerNotesPermission";
import { TLearnerNotesForm, useLearnerNotesSchema } from "@/features/learning-progress/schema/useLearnerNotesSchema";
import { text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

const PAGE_WIDTH = 1150;
const CONTENT_WIDTH = "900px";

export const LearningProgressLearnerEditPage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { learnerId } = useParams();
  const { data: learnerDetailRes } = useLearningProgressLearnerDetail({ learnerId: learnerId ?? "" });
  const learnerInfo = learnerDetailRes?.data.learnerInfo;
  const notes = learnerDetailRes?.data.notes;
  const { companyReport, memo } = useLearnerNotesPermission();

  // ラベルはユーザー種別で変わりうるためスキーマフックへ注入する (エラーメッセージの「」内に表示される)
  // メモのラベルは種別で切り替わる: RSO →「登録支援機関内メモ」/ Company →「社内メモ」
  const labels = useMemo(
    () => ({
      encouragementMsg: "ご本人への応援メッセージ",
      companyReport: "報告内容",
      rsoMemo: memo.title,
    }),
    [memo.title],
  );
  const schema = useLearnerNotesSchema(labels);

  const methods = useForm<TLearnerNotesForm>({
    defaultValues: { encouragementMsg: "", companyReport: "", rsoMemo: "" },
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [initialValues, setInitialValues] = useState<Partial<TLearnerNotesForm>>();
  const hasSeededRef = useRef(false);

  // Seed form once from server — subsequent refetches must not overwrite user edits
  useEffect(() => {
    if (hasSeededRef.current || !notes) return;
    const seed = {
      encouragementMsg: notes.encouragementMsg ?? "",
      companyReport: notes.companyReport ?? "",
      rsoMemo: notes.rsoMemo ?? "",
    };
    methods.reset(seed);
    setInitialValues(seed);
    hasSeededRef.current = true;
  }, [notes, methods]);

  const handleSave = async (data: TLearnerNotesForm) => {
    if (!learnerId || isSaving) return;
    setIsSaving(true);
    let success = false;
    try {
      // Send all 3 fields; fall back to server value for fields this role cannot edit
      await updateLearnerNotesApi(learnerId, {
        encouragementMsg: data.encouragementMsg ?? "",
        companyReport: companyReport.editable ? data.companyReport ?? "" : notes?.companyReport ?? "",
        rsoMemo: memo.editable ? data.rsoMemo ?? "" : notes?.rsoMemo ?? "",
      });
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_DETAIL, learnerId],
      });
      success = true;
    } catch (e) {
      showError(e);
    } finally {
      setIsSaving(false);
    }
    if (success) navigate(-1);
  };

  return (
    <>
      <TitleContainer>
        <Grid container direction="column" gap={1}>
          <Typography variant="head20Bold">
            学習進捗{learnerInfo ? ` - ${learnerInfo.fullName}` : ""}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography
              variant="body14"
              color={text.secondary}
              sx={{ display: "flex", alignItems: "center" }}
            >
              登録日：<span>{learnerInfo?.registeredAt ?? ""}</span>
            </Typography>
            <Typography
              variant="body14"
              color={text.secondary}
              sx={{ display: "flex", alignItems: "center" }}
            >
              更新日：<span>{learnerInfo?.updatedAt ?? ""}</span>
            </Typography>
          </Box>
        </Grid>
      </TitleContainer>

      <Box sx={{ width: `${PAGE_WIDTH}px`, margin: "0 auto" }}>
        <Form methods={methods} schema={schema} onSubmit={handleSave} initialValues={initialValues}>
          <MenuNavigate
            menuPrefix={
              <Typography variant="body14Bold" sx={{ mb: 1 }}>
                {learnerInfo?.fullName ?? ""}
              </Typography>
            }
            contents={[
              {
                title: "外国人情報",
                content: learnerInfo ? <LearnerInfoReadOnlySection data={learnerInfo} /> : null,
              },
              {
                title: "外国人本人宛",
                content: notes ? <EncouragementMessageEditSection /> : null,
              },
              ...(companyReport.visible
                ? [
                    {
                      title: "所属機関様への報告",
                      content: notes ? (
                        <OrganizationReportEditSection readOnly={!companyReport.editable} />
                      ) : null,
                    },
                  ]
                : []),
              ...(memo.visible
                ? [
                    {
                      title: memo.title,
                      content: notes ? (
                        <SupportOrgMemoEditSection
                          readOnly={!memo.editable}
                          title={memo.title}
                          subtitle={memo.subtitle}
                        />
                      ) : null,
                    },
                  ]
                : []),
            ]}
            contentWidth={CONTENT_WIDTH}
            contentAffix={
              <FooterContainer>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                    disabled={isSaving}
                  >
                    戻る
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    startIcon={<IconSave />}
                    disabled={isSaving || !notes}
                  >
                    保存
                  </Button>
                </Stack>
              </FooterContainer>
            }
          />
        </Form>
      </Box>
    </>
  );
};

const TitleContainer = styled(Stack)`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: self-start;
  width: ${PAGE_WIDTH}px;
  margin: 0 auto;
  margin-bottom: 16px;
`;
